import React, { useState, useEffect } from 'react';
import Modal from '../components/UI/Modal';
import { 
  DollarSign, 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Eye,
  Edit,
  Send,
  Filter,
  Search,
  Download,
  Upload,
  Calendar,
  Users,
  Building,
  Activity,
  TrendingUp,
  BarChart3,
  RefreshCw,
  Plus,
  Settings,
  ChevronDown,
  Shield,
  Globe,
  Trash2
} from 'lucide-react';
import { staffAPI } from '../services/api';
import NSMakeUpPayTab from '../components/government/NSMakeUpPayTab';
import LeaveReimbursementTracker from '../components/government/LeaveReimbursementTracker';
import LevyWaiverTracker from '../components/government/LevyWaiverTracker';
import { useTabPersistence, useNestedTabPersistence } from '../hooks/usePersistence';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { StatusBadge } from '../components/UI/StatusBadge';
import { CountBadge } from '../components/UI/CountBadge';
import FormSelect from '../components/UI/FormSelect';
import PillSelect from '../components/UI/PillSelect';
import { formatDate, formatDateTime, getTodayInSingaporeTimezone } from '../utils/dateUtils';
import toast from '../utils/toast';

const GovernmentReimbursement = () => {
  // Main navigation state with persistence
  const validMainTabs = ['leave-reimbursement', 'levy-waiver', 'ns-makeup'];
  const [mainTab, setMainTab] = useTabPersistence('government-reimbursement-main', 'leave-reimbursement', validMainTabs);
  
  // Sub-tab uses nested persistence to avoid URL parameter collision with main tab
  const validSubTabs = ['dashboard', 'claims', 'applications', 'reports', 'settings'];
  const { subTab: subTabState } = useNestedTabPersistence('government-reimbursement', mainTab, {
    subTab: { 
      default: 'dashboard', 
      valid: validSubTabs, 
      param: 'subTab' 
    }
  });
  const [activeTab, setActiveTab] = subTabState;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard data
  const [dashboardSummary, setDashboardSummary] = useState({
    summary: {},
    amounts: {},
    type_breakdown: {}
  });

  // Claims data
  const [claims, setClaims] = useState([]);
  const [filteredClaims, setFilteredClaims] = useState([]);
  const [overdueClaims, setOverdueClaims] = useState([]);

  // Levy Waiver data
  const [levyWaivers, setLevyWaivers] = useState([]);
  const [filteredLevyWaivers, setFilteredLevyWaivers] = useState([]);
  const [levyDashboard, setLevyDashboard] = useState({});

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('all');

  // Selection for bulk operations
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [showBulkSubmit, setShowBulkSubmit] = useState(false);

  // Modal states
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showClaimDetails, setShowClaimDetails] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending', label: 'Pending Submission' },
    { value: 'submitted', label: 'Submitted to Government' },
    { value: 'approved', label: 'Approved' },
    { value: 'paid', label: 'Reimbursement Received' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'gpml', label: 'GPML - Maternity Leave' },
    { value: 'gppl', label: 'GPPL - Paternity Leave' },
    { value: 'gpcl', label: 'GPCL - Childcare Leave' },
    { value: 'spl', label: 'SPL - Shared Parental Leave' },
    { value: 'gpal', label: 'GPAL - Adoption Leave' }
  ];

  const levyWaiverTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'overseas_leave', label: 'Overseas Leave' },
    { value: 'hospitalisation', label: 'Hospitalisation Leave' },
    { value: 'onboard_programme', label: 'Onboard Programme' },
    { value: 'police_custody', label: 'Police/Embassy Custody' },
    { value: 'death', label: 'Death' },
    { value: 'failed_return', label: 'Failed to Return' }
  ];

  useEffect(() => {
    loadInitialData();
  }, [mainTab]);

  useEffect(() => {
    if (mainTab === 'leave-reimbursement') {
      filterClaims();
    } else {
      filterLevyWaivers();
    }
  }, [claims, levyWaivers, searchTerm, statusFilter, typeFilter, dateRangeFilter, mainTab]);

  const loadInitialData = async () => {
    // NS make-up pay tab self-manages its data via React Query.
    if (mainTab === 'ns-makeup') {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      if (mainTab === 'leave-reimbursement') {
        await Promise.all([
          loadDashboardSummary(),
          loadClaims(),
          loadOverdueClaims()
        ]);
      } else {
        await Promise.all([
          loadLevyWaivers(),
          loadLevyDashboard()
        ]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
      toast.error(`Failed to load ${mainTab === 'leave-reimbursement' ? 'reimbursement' : 'levy waiver'} data`);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardSummary = async () => {
    try {
      const response = await staffAPI.get('/government-reimbursement/dashboard_summary/');
      setDashboardSummary(response.data);
    } catch (error) {
      console.error('Error loading dashboard summary:', error);
    }
  };

  const loadClaims = async () => {
    try {
      const response = await staffAPI.get('/government-reimbursement/');
      setClaims(response.data.results || response.data);
    } catch (error) {
      console.error('Error loading claims:', error);
    }
  };

  const loadOverdueClaims = async () => {
    try {
      const response = await staffAPI.get('/government-reimbursement/overdue_claims/');
      setOverdueClaims(response.data.claims || []);
    } catch (error) {
      console.error('Error loading overdue claims:', error);
    }
  };

  const loadLevyWaivers = async () => {
    try {
      const response = await staffAPI.get('/levy-waiver/');
      setLevyWaivers(response.data.results || response.data);
    } catch (error) {
      console.error('Error loading levy waivers:', error);
    }
  };

  const loadLevyDashboard = async () => {
    try {
      const response = await staffAPI.get('/levy-waiver/dashboard_summary/');
      setLevyDashboard(response.data);
    } catch (error) {
      console.error('Error loading levy dashboard:', error);
    }
  };

  const filterClaims = () => {
    let filtered = [...claims];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(claim => 
        claim.employee_details?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.employee_details?.staff_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.claim_reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        claim.reimbursement_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(claim => claim.claim_status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(claim => claim.reimbursement_type === typeFilter);
    }

    // Date range filter
    if (dateRangeFilter !== 'all') {
      const now = new Date();
      let cutoffDate;
      
      switch (dateRangeFilter) {
        case 'last_7_days':
          cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'last_30_days':
          cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'last_90_days':
          cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          cutoffDate = null;
      }
      
      if (cutoffDate) {
        filtered = filtered.filter(claim => 
          new Date(claim.created_at) >= cutoffDate
        );
      }
    }

    setFilteredClaims(filtered);
  };

  const filterLevyWaivers = () => {
    let filtered = [...levyWaivers];

    if (searchTerm) {
      filtered = filtered.filter(waiver => 
        waiver.employee_details?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        waiver.employee_details?.staff_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        waiver.waiver_reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        waiver.waiver_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(waiver => waiver.waiver_status === statusFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(waiver => waiver.waiver_type === typeFilter);
    }

    if (dateRangeFilter !== 'all') {
      const now = new Date();
      let cutoffDate;
      
      switch (dateRangeFilter) {
        case 'last_7_days':
          cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'last_30_days':
          cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'last_90_days':
          cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          cutoffDate = null;
      }
      
      if (cutoffDate) {
        filtered = filtered.filter(waiver => 
          new Date(waiver.created_at) >= cutoffDate
        );
      }
    }

    setFilteredLevyWaivers(filtered);
  };

  const handleLevyWaiverSubmit = async (waiverId, submissionData) => {
    try {
      const response = await staffAPI.post(
        `/levy-waiver/${waiverId}/submit_to_mom/`,
        submissionData
      );
      
      toast.success('Waiver submitted successfully');
      loadInitialData(); // Refresh data
    } catch (error) {
      console.error('Error submitting waiver:', error);
      toast.error('Failed to submit waiver');
    }
  };

  const handleLevyWaiverStatusUpdate = async (waiverId, statusData) => {
    try {
      const response = await staffAPI.post(
        `/levy-waiver/${waiverId}/update_status/`,
        statusData
      );
      
      toast.success(`Waiver status updated to ${statusData.waiver_status}`);
      loadInitialData(); // Refresh data
    } catch (error) {
      console.error('Error updating waiver status:', error);
      toast.error('Failed to update waiver status');
    }
  };

  const handleBulkLevySubmit = async (submissionData) => {
    try {
      const response = await staffAPI.post('/levy-waiver/bulk_submit/', {
        waiver_ids: selectedClaims,
        submitted_date: submissionData.submitted_date
      });
      
      toast.success(`${response.data.submitted_count} waivers submitted successfully`);
      setSelectedClaims([]);
      loadInitialData(); // Refresh data
    } catch (error) {
      console.error('Error bulk submitting waivers:', error);
      toast.error('Failed to submit waivers');
    }
  };

  const handleBulkSubmit = async (submissionData) => {
    try {
      const response = await staffAPI.post('/government-reimbursement/bulk_submit/', {
        claim_ids: selectedClaims,
        submitted_date: submissionData.submitted_date
      });
      
      toast.success(`${response.data.submitted_count} claims submitted successfully`);
      setSelectedClaims([]);
      setShowBulkSubmit(false);
      loadInitialData(); // Refresh data
    } catch (error) {
      console.error('Error bulk submitting claims:', error);
      toast.error('Failed to submit claims');
    }
  };

  const handleSubmitClaim = async (claimId, submissionData) => {
    try {
      const response = await staffAPI.post(
        `/government-reimbursement/${claimId}/submit_to_government/`,
        submissionData
      );
      
      toast.success('Claim submitted successfully');
      setShowSubmitModal(false);
      loadInitialData(); // Refresh data
    } catch (error) {
      console.error('Error submitting claim:', error);
      toast.error('Failed to submit claim');
    }
  };

  const handleUpdateStatus = async (claimId, statusData) => {
    try {
      const response = await staffAPI.post(
        `/government-reimbursement/${claimId}/update_status/`,
        statusData
      );
      
      toast.success(`Claim status updated to ${statusData.claim_status}`);
      loadInitialData(); // Refresh data
    } catch (error) {
      console.error('Error updating claim status:', error);
      toast.error('Failed to update claim status');
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="w-full">
          {/* Header Skeleton */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="h-7 bg-gray-200 rounded w-48 animate-pulse"></div>
                <div className="h-4 bg-gray-200 rounded w-80 animate-pulse"></div>
              </div>
              <div className="flex gap-3">
                <div className="h-10 bg-gray-200 rounded-full w-32 animate-pulse"></div>
                <div className="h-10 bg-gray-200 rounded-full w-40 animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Main Tabs Skeleton */}
          <div className="mb-6 flex items-center gap-2 flex-wrap">
            {[1, 2].map(i => (
              <div key={i} className="h-10 bg-gray-200 rounded-full w-40 animate-pulse"></div>
            ))}
          </div>

          {/* Stats Cards Skeleton */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                <div className="flex items-center animate-pulse">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                  <div className="ml-2 sm:ml-4 space-y-2 min-w-0">
                    <div className="h-6 bg-gray-200 rounded w-20"></div>
                    <div className="h-4 bg-gray-200 rounded w-28"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Content Area Skeleton */}
          <div className="bg-white rounded-2xl shadow-sm border">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-40"></div>
                <div className="h-10 bg-gray-200 rounded w-64"></div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg animate-pulse">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                      <div className="h-3 bg-gray-200 rounded w-24"></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 mb-1 min-w-0">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                </div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Government Relations</h1>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">
                Manage leave reimbursements, foreign worker levy waivers, and NS make-up pay claims
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              {false && mainTab === 'levy-waiver' && (
                <button className="btn btn-success self-start sm:self-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  New Waiver Application
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Tabs - Admin pill buttons */}
        <div className="mb-6 flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
          {[
            { 
              id: 'leave-reimbursement', 
              name: 'Leave Reimbursement', 
              icon: DollarSign,
              description: 'GPML, GPPL, GPCL Claims'
            },
            { 
              id: 'levy-waiver', 
              name: 'Levy Waiver', 
              icon: Shield,
              description: 'Foreign Worker Levy Waivers'
            },
            { 
              id: 'ns-makeup', 
              name: 'NS Leave', 
              icon: Activity,
              description: 'NS Make-Up Pay Claims'
            }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setMainTab(tab.id);
                  setActiveTab('dashboard');
                  setSelectedClaims([]);
                }}
                className={`tab-btn-admin ${mainTab === tab.id ? 'active' : ''}`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* Leave Reimbursement Section — tracker derived from approved leave applications */}
        {mainTab === 'leave-reimbursement' && (
          <LeaveReimbursementTracker />
        )}

        {/* Legacy leave-reimbursement UI — superseded by LeaveReimbursementTracker, kept dormant */}
        {false && (
          <>
            {/* Quick Actions */}
            <div className="mb-6 flex flex-wrap gap-3">
              {selectedClaims.length > 0 && (
                <button
                  onClick={() => setShowBulkSubmit(true)}
                  className="btn btn-success"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Bulk Submit ({selectedClaims.length})
                </button>
              )}
            </div>

            {/* Sub Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
              <button onClick={() => setActiveTab('dashboard')} className={`tab-btn-admin tab-btn-admin-sm ${activeTab === 'dashboard' ? 'active' : ''}`}>
                <BarChart3 className="w-4 h-4" />
                Dashboard
              </button>
              <button onClick={() => setActiveTab('claims')} className={`tab-btn-admin tab-btn-admin-sm ${activeTab === 'claims' ? 'active' : ''}`}>
                <FileText className="w-4 h-4" />
                All Claims
                {claims.length > 0 && <CountBadge count={claims.length} theme="admin" active={activeTab === 'claims'} />}
              </button>
              <button onClick={() => setActiveTab('overdue')} className={`tab-btn-admin tab-btn-admin-sm ${activeTab === 'overdue' ? 'active' : ''}`}>
                <AlertTriangle className="w-4 h-4" />
                Overdue Claims
                {overdueClaims.length > 0 && <CountBadge count={overdueClaims.length} theme="admin" active={activeTab === 'overdue'} />}
              </button>
            </div>

            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                  <div className="dashboard-card">
                    <div className="dashboard-card-icon">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div className="dashboard-card-title">Total Claims</div>
                    <div className="dashboard-card-value">
                      {dashboardSummary.summary?.total_claims || 0}
                    </div>
                  </div>

                  <div className="dashboard-card">
                    <div className="dashboard-card-icon">
                      <Clock className="w-6 h-6" />
                    </div>
                    <div className="dashboard-card-title">Pending Claims</div>
                    <div className="dashboard-card-value">
                      {dashboardSummary.summary?.pending_claims || 0}
                    </div>
                  </div>

                  <div className="dashboard-card">
                    <div className="dashboard-card-icon">
                      <CheckCircle className="w-6 h-6" />
                    </div>
                    <div className="dashboard-card-title">Approved Claims</div>
                    <div className="dashboard-card-value">
                      {dashboardSummary.summary?.approved_claims || 0}
                    </div>
                  </div>

                  <div className="dashboard-card">
                    <div className="dashboard-card-icon">
                      <DollarSign className="w-6 h-6" />
                    </div>
                    <div className="dashboard-card-title">Total Amount</div>
                    <div className="dashboard-card-value">
                      {formatAmount(dashboardSummary.amounts?.total_amount)}
                    </div>
                  </div>
                </div>

                {/* Type Breakdown */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="text-lg font-medium text-gray-900">
                      Reimbursement by Type
                    </h3>
                  </div>
                  <div className="card-content">
                    <div className="space-y-4">
                      {Object.entries(dashboardSummary.type_breakdown || {}).map(([type, data]) => (
                        <div key={type} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 bg-red-500 rounded mr-3"></div>
                            <span className="text-sm font-medium text-gray-700">
                              {data.label} ({data.count} claims)
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {formatAmount(data.total_amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* MOM Entitlement Reference (verified against mom.gov.sg) */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Shield className="w-4 h-4 mr-2 text-red-600" />
                      MOM Reimbursement Reference
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Entitlements depend on the child's date of birth (DOB) / estimated delivery
                      date. Maternity &amp; adoption leave is capped at <strong>S$10,000 per 4 weeks</strong>,
                      paternity &amp; shared parental at <strong>S$2,500/week</strong> (per-day cap = S$2,500 ÷
                      working days/week); childcare leave is capped at <strong>S$500/day</strong>.
                    </p>
                  </div>
                  <div className="card-content">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <div className="font-medium text-gray-900 mb-1">Paternity Leave (GPPL)</div>
                        <ul className="text-gray-600 space-y-1 list-disc list-inside">
                          <li>DOB before 1 Apr 2025: 2 weeks (+2 weeks if employer agrees)</li>
                          <li>DOB on/after 1 Apr 2025: <strong>4 weeks</strong> (mandatory)</li>
                        </ul>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <div className="font-medium text-gray-900 mb-1">Shared Parental Leave (SPL)</div>
                        <ul className="text-gray-600 space-y-1 list-disc list-inside">
                          <li>DOB 1 Apr 2025 – 31 Mar 2026: <strong>6 weeks</strong> shared</li>
                          <li>DOB on/after 1 Apr 2026: <strong>10 weeks</strong> shared</li>
                          <li>Take within 12 months of DOB</li>
                        </ul>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <div className="font-medium text-gray-900 mb-1">Maternity Leave (GPML)</div>
                        <ul className="text-gray-600 space-y-1 list-disc list-inside">
                          <li>16 weeks for SC child</li>
                          <li>1st/2nd child: last 8 weeks govt-paid; 3rd+: all 16 weeks</li>
                        </ul>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <div className="font-medium text-gray-900 mb-1">Childcare Leave (CCL — child under 7)</div>
                        <ul className="text-gray-600 space-y-1 list-disc list-inside">
                          <li>Up to 6 days/yr (2 SC parents)</li>
                          <li>Days 1–3: employer-paid at gross rate</li>
                          <li>Days 4–6: <strong>govt-reimbursed</strong>, capped S$500/day (up to <strong>S$1,500/yr</strong>)</li>
                        </ul>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <div className="font-medium text-gray-900 mb-1">Extended Childcare Leave (ECCL — child 7–12)</div>
                        <ul className="text-gray-600 space-y-1 list-disc list-inside">
                          <li>2 days/yr</li>
                          <li>Both days <strong>fully govt-reimbursed</strong>, capped S$500/day (up to <strong>S$1,000/yr</strong>)</li>
                        </ul>
                      </div>
                    </div>

                    {/* Claim submission deadline (GPL Portal, profamilyleave.msf.gov.sg) */}
                    <div className="mt-4 flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-200 p-4">
                      <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-900">
                        <strong>Submission deadline:</strong> Claims for GPML, GPPL, SPL and GPCL
                        must be submitted <strong>no later than 3 months after the last day of the
                        leave</strong>. Claims past this window are flagged as overdue.
                        <span className="block text-xs text-amber-700 mt-1">
                          Source: Government-Paid Leave (GPL) Portal — profamilyleave.msf.gov.sg
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Claims Tab */}
            {activeTab === 'claims' && (
              <div className="space-y-6">
                {/* Filters */}
                <div className="card">
                  <div className="card-content">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="form-group">
                        <label className="form-label">
                          <Search className="w-4 h-4 inline mr-2" />
                          Search
                        </label>
                        <div className="relative">
                          <Search className="input-icon" />
                          <input
                            type="text"
                            placeholder="Search claims..."
                            className="input-admin input-with-icon"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          <Activity className="w-4 h-4 inline mr-2" />
                          Status
                        </label>
                        <PillSelect
                          options={statusOptions}
                          value={statusFilter}
                          onChange={setStatusFilter}
                          theme="admin"
                          icon={Activity}
                          iconColor="text-red-600"
                          className="w-full"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          <FileText className="w-4 h-4 inline mr-2" />
                          Type
                        </label>
                        <PillSelect
                          options={typeOptions}
                          value={typeFilter}
                          onChange={setTypeFilter}
                          theme="admin"
                          icon={FileText}
                          iconColor="text-red-600"
                          className="w-full"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          <Calendar className="w-4 h-4 inline mr-2" />
                          Date Range
                        </label>
                        <PillSelect
                          options={[
                            { value: 'all', label: 'All Time' },
                            { value: 'last_7_days', label: 'Last 7 Days' },
                            { value: 'last_30_days', label: 'Last 30 Days' },
                            { value: 'last_90_days', label: 'Last 90 Days' },
                          ]}
                          value={dateRangeFilter}
                          onChange={setDateRangeFilter}
                          theme="admin"
                          icon={Calendar}
                          iconColor="text-red-600"
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Claims Table */}
                <div className="card">
                  {/* Mobile card list */}
                  <div className="md:hidden p-4 space-y-3">
                    {filteredClaims.map((claim) => (
                      <div key={claim.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <input
                              type="checkbox"
                              className="rounded mt-0.5 flex-shrink-0"
                              checked={selectedClaims.includes(claim.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedClaims([...selectedClaims, claim.id]);
                                } else {
                                  setSelectedClaims(selectedClaims.filter(id => id !== claim.id));
                                }
                              }}
                              aria-label="Select claim"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {claim.employee_details?.full_name || 'N/A'}
                              </p>
                              <p className="text-xs text-gray-500">{claim.employee_details?.staff_id}</p>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <StatusBadge
                              status={claim.claim_status === 'paid' ? 'completed' : claim.claim_status}
                              label={claim.claim_status === 'paid' ? 'Reimbursement Received' : undefined}
                            />
                          </div>
                        </div>
                        <div className="mt-3 space-y-2 flex-1">
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Type</span>
                            <span className="text-gray-900 text-right truncate min-w-0">
                              {typeOptions.find(t => t.value === claim.reimbursement_type)?.label || claim.reimbursement_type}
                            </span>
                          </div>
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Amount</span>
                            <span className="text-gray-900 text-right truncate min-w-0 font-semibold tabular-nums">
                              {formatAmount(claim.total_reimbursement_amount)}
                            </span>
                          </div>
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Created</span>
                            <span className="text-gray-900 text-right truncate min-w-0">
                              {formatDate(claim.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="card-action-footer">
                          <button
                            onClick={() => {
                              setSelectedClaim(claim);
                              setShowClaimDetails(true);
                            }}
                            className="icon-button-admin"
                            title="View Details"
                            aria-label="View claim details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {claim.claim_status === 'pending' && (
                            <button
                              onClick={() => {
                                setSelectedClaim(claim);
                                setShowSubmitModal(true);
                              }}
                              className="text-green-600 hover:text-green-900 hover:bg-green-50 p-2 rounded-full transition-all duration-200"
                              title="Submit to Government"
                              aria-label="Submit claim to government"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">
                            <input
                              type="checkbox"
                              className="rounded"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedClaims(filteredClaims.map(claim => claim.id));
                                } else {
                                  setSelectedClaims([]);
                                }
                              }}
                            />
                          </th>
                          <th className="table-header-cell">Employee</th>
                          <th className="table-header-cell">Type</th>
                          <th className="table-header-cell">Amount</th>
                          <th className="table-header-cell">Status</th>
                          <th className="table-header-cell">Created</th>
                          <th className="table-header-cell">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {filteredClaims.map((claim) => (
                          <tr key={claim.id} className="table-row">
                            <td className="table-cell">
                              <input
                                type="checkbox"
                                className="rounded"
                                checked={selectedClaims.includes(claim.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedClaims([...selectedClaims, claim.id]);
                                  } else {
                                    setSelectedClaims(selectedClaims.filter(id => id !== claim.id));
                                  }
                                }}
                              />
                            </td>
                            <td className="table-cell">
                              <div className="text-sm font-medium text-gray-900">
                                {claim.employee_details?.full_name || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {claim.employee_details?.staff_id}
                              </div>
                            </td>
                            <td className="table-cell">
                              {typeOptions.find(t => t.value === claim.reimbursement_type)?.label || claim.reimbursement_type}
                            </td>
                            <td className="table-cell">
                              {formatAmount(claim.total_reimbursement_amount)}
                            </td>
                            <td className="table-cell">
                              <StatusBadge
                                status={claim.claim_status === 'paid' ? 'completed' : claim.claim_status}
                                label={claim.claim_status === 'paid' ? 'Reimbursement Received' : undefined}
                              />
                            </td>
                            <td className="table-cell">
                              {formatDate(claim.created_at)}
                            </td>
                            <td className="table-cell">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => {
                                    setSelectedClaim(claim);
                                    setShowClaimDetails(true);
                                  }}
                                  className="icon-button-admin"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                {claim.claim_status === 'pending' && (
                                  <button
                                    onClick={() => {
                                      setSelectedClaim(claim);
                                      setShowSubmitModal(true);
                                    }}
                                    className="text-green-600 hover:text-green-900 hover:bg-green-50 p-2 rounded-full transition-all duration-200"
                                    title="Submit to Government"
                                  >
                                    <Send className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Overdue Claims Tab */}
            {activeTab === 'overdue' && (
              <div className="space-y-6">
                <div className="alert alert-error">
                  <AlertTriangle className="alert-icon" />
                  <div>
                    <h3 className="text-sm font-medium">
                      Attention Required
                    </h3>
                    <div className="mt-2 text-sm">
                      <p>
                        You have {overdueClaims.length} claims pending for more than 30 days.
                        Please review and submit them to avoid delays in reimbursement.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Overdue Claims List */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-red-600" />
                      Overdue Claims
                    </h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {overdueClaims.map((claim) => (
                      <div key={claim.id} className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900 flex items-center">
                              <Users className="w-4 h-4 mr-2 text-gray-400" />
                              {claim.employee_details?.full_name} - {claim.reimbursement_type.toUpperCase()}
                            </p>
                            <p className="text-sm text-gray-500 flex items-center mt-1">
                              <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                              Created {claim.days_since_created} days ago • 
                              <DollarSign className="w-4 h-4 ml-2 mr-1 text-gray-400" />
                              {formatAmount(claim.total_reimbursement_amount)}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedClaim(claim);
                              setShowSubmitModal(true);
                            }}
                            className="btn btn-danger"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            Submit Now
                          </button>
                        </div>
                      </div>
                    ))}
                    {overdueClaims.length === 0 && (
                      <div className="p-8 text-center">
                        <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No overdue claims</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          All claims are up to date. Great job!
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Levy Waiver Section — tracker derived from approved leave applications */}
        {mainTab === 'levy-waiver' && (
          <LevyWaiverTracker />
        )}

        {/* Legacy levy-waiver manual UI — superseded by LevyWaiverTracker, kept dormant */}
        {false && mainTab === 'levy-waiver' && (
          <>
            {/* Quick Actions */}
            <div className="mb-6 flex flex-wrap gap-3">
              {/* Additional actions can be added here if needed */}
            </div>

            {/* Sub Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
              <button onClick={() => setActiveTab('dashboard')} className={`tab-btn-admin tab-btn-admin-sm ${activeTab === 'dashboard' ? 'active' : ''}`}>
                <BarChart3 className="w-4 h-4" />
                Dashboard
              </button>
              <button onClick={() => setActiveTab('waivers')} className={`tab-btn-admin tab-btn-admin-sm ${activeTab === 'waivers' ? 'active' : ''}`}>
                <Shield className="w-4 h-4" />
                All Waivers
                {levyWaivers.length > 0 && <CountBadge count={levyWaivers.length} theme="admin" active={activeTab === 'waivers'} />}
              </button>
              <button onClick={() => setActiveTab('overseas')} className={`tab-btn-admin tab-btn-admin-sm ${activeTab === 'overseas' ? 'active' : ''}`}>
                <Globe className="w-4 h-4" />
                Overseas Leave
              </button>
              <button onClick={() => setActiveTab('medical')} className={`tab-btn-admin tab-btn-admin-sm ${activeTab === 'medical' ? 'active' : ''}`}>
                <Activity className="w-4 h-4" />
                Medical Cases
              </button>
              <button onClick={() => setActiveTab('documents')} className={`tab-btn-admin tab-btn-admin-sm ${activeTab === 'documents' ? 'active' : ''}`}>
                <FileText className="w-4 h-4" />
                Documents
              </button>
            </div>

            {/* Levy Waiver Dashboard */}
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                  <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                    <div className="flex items-start">
                      <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                      </div>
                      <div className="ml-2 sm:ml-4 min-w-0">
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">{levyDashboard.summary?.total_waivers || 0}</p>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Total Waivers</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                    <div className="flex items-start">
                      <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
                      </div>
                      <div className="ml-2 sm:ml-4 min-w-0">
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">{levyDashboard.summary?.pending_waivers || 0}</p>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Pending</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                    <div className="flex items-start">
                      <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
                      </div>
                      <div className="ml-2 sm:ml-4 min-w-0">
                        <p className="text-lg sm:text-2xl font-bold text-gray-900">{levyDashboard.summary?.approved_waivers || 0}</p>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Approved</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{formatAmount(levyDashboard.amounts?.total_savings)}</p>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Total Savings</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Levy Waiver Types Breakdown */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="text-lg font-medium text-gray-900">
                      Waiver Applications by Type
                    </h3>
                  </div>
                  <div className="card-content">
                    <div className="space-y-4">
                      {levyWaiverTypes.slice(1).map((type) => (
                        <div key={type.value} className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-4 h-4 bg-blue-500 rounded mr-3"></div>
                            <span className="text-sm font-medium text-gray-700">
                              {type.label}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {levyDashboard.type_breakdown?.[type.value]?.count || 0} cases
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* All Waivers Tab */}
            {activeTab === 'waivers' && (
              <div className="space-y-6">
                {/* Filters */}
                <div className="card">
                  <div className="card-content">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="form-group">
                        <label className="form-label">
                          <Search className="w-4 h-4 inline mr-2" />
                          Search
                        </label>
                        <div className="relative">
                          <Search className="input-icon" />
                          <input
                            type="text"
                            placeholder="Search waivers..."
                            className="input-admin input-with-icon"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          <Activity className="w-4 h-4 inline mr-2" />
                          Status
                        </label>
                        <PillSelect
                          options={statusOptions}
                          value={statusFilter}
                          onChange={setStatusFilter}
                          theme="admin"
                          icon={Activity}
                          iconColor="text-red-600"
                          className="w-full"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          <Shield className="w-4 h-4 inline mr-2" />
                          Type
                        </label>
                        <PillSelect
                          options={levyWaiverTypes}
                          value={typeFilter}
                          onChange={setTypeFilter}
                          theme="admin"
                          icon={FileText}
                          iconColor="text-red-600"
                          className="w-full"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          <Calendar className="w-4 h-4 inline mr-2" />
                          Date Range
                        </label>
                        <PillSelect
                          options={[
                            { value: 'all', label: 'All Time' },
                            { value: 'last_7_days', label: 'Last 7 Days' },
                            { value: 'last_30_days', label: 'Last 30 Days' },
                            { value: 'last_90_days', label: 'Last 90 Days' },
                          ]}
                          value={dateRangeFilter}
                          onChange={setDateRangeFilter}
                          theme="admin"
                          icon={Calendar}
                          iconColor="text-red-600"
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Waivers Table */}
                <div className="card">
                  {/* Mobile card list */}
                  <div className="md:hidden p-4 space-y-3">
                    {filteredLevyWaivers.map((waiver) => (
                      <div key={waiver.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0">
                            <input
                              type="checkbox"
                              className="rounded mt-0.5 flex-shrink-0"
                              checked={selectedClaims.includes(waiver.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedClaims([...selectedClaims, waiver.id]);
                                } else {
                                  setSelectedClaims(selectedClaims.filter(id => id !== waiver.id));
                                }
                              }}
                              aria-label="Select waiver"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {waiver.employee_details?.full_name || 'N/A'}
                              </p>
                              <p className="text-xs text-gray-500">{waiver.employee_details?.staff_id}</p>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <StatusBadge
                              status={waiver.waiver_status === 'paid' ? 'completed' : waiver.waiver_status}
                              label={waiver.waiver_status === 'paid' ? 'Reimbursement Received' : undefined}
                            />
                          </div>
                        </div>
                        <div className="mt-3 space-y-2 flex-1">
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Type</span>
                            <span className="text-gray-900 text-right truncate min-w-0">
                              {levyWaiverTypes.find(t => t.value === waiver.waiver_type)?.label || waiver.waiver_type}
                            </span>
                          </div>
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Period</span>
                            <span className="text-gray-900 text-right min-w-0">
                              {formatDate(waiver.waiver_start_date)} - {formatDate(waiver.waiver_end_date)}
                              <span className="block text-xs text-gray-500">{waiver.waiver_total_days} days</span>
                            </span>
                          </div>
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Amount</span>
                            <span className="text-gray-900 text-right truncate min-w-0 font-semibold tabular-nums">
                              {formatAmount(waiver.levy_amount_waived)}
                            </span>
                          </div>
                          <div className="flex items-start justify-between gap-3 text-sm">
                            <span className="text-gray-500 flex-shrink-0">Created</span>
                            <span className="text-gray-900 text-right truncate min-w-0">
                              {formatDate(waiver.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="card-action-footer">
                          <button
                            onClick={() => {
                              setSelectedClaim(waiver);
                              setShowClaimDetails(true);
                            }}
                            className="icon-button-admin"
                            title="View Details"
                            aria-label="View waiver details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {waiver.waiver_status === 'pending' && (
                            <button
                              onClick={() => {
                                setSelectedClaim(waiver);
                                setShowSubmitModal(true);
                              }}
                              className="text-green-600 hover:text-green-900 hover:bg-green-50 p-2 rounded-full transition-all duration-200"
                              title="Submit to MOM"
                              aria-label="Submit waiver to MOM"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">
                            <input
                              type="checkbox"
                              className="rounded"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedClaims(filteredLevyWaivers.map(waiver => waiver.id));
                                } else {
                                  setSelectedClaims([]);
                                }
                              }}
                            />
                          </th>
                          <th className="table-header-cell">Employee</th>
                          <th className="table-header-cell">Type</th>
                          <th className="table-header-cell">Period</th>
                          <th className="table-header-cell">Amount</th>
                          <th className="table-header-cell">Status</th>
                          <th className="table-header-cell">Created</th>
                          <th className="table-header-cell">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {filteredLevyWaivers.map((waiver) => (
                          <tr key={waiver.id} className="table-row">
                            <td className="table-cell">
                              <input
                                type="checkbox"
                                className="rounded"
                                checked={selectedClaims.includes(waiver.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedClaims([...selectedClaims, waiver.id]);
                                  } else {
                                    setSelectedClaims(selectedClaims.filter(id => id !== waiver.id));
                                  }
                                }}
                              />
                            </td>
                            <td className="table-cell">
                              <div className="text-sm font-medium text-gray-900">
                                {waiver.employee_details?.full_name || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {waiver.employee_details?.staff_id}
                              </div>
                            </td>
                            <td className="table-cell">
                              {levyWaiverTypes.find(t => t.value === waiver.waiver_type)?.label || waiver.waiver_type}
                            </td>
                            <td className="table-cell">
                              <div className="text-sm">
                                {formatDate(waiver.waiver_start_date)} - {formatDate(waiver.waiver_end_date)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {waiver.waiver_total_days} days
                              </div>
                            </td>
                            <td className="table-cell">
                              {formatAmount(waiver.levy_amount_waived)}
                            </td>
                            <td className="table-cell">
                              <StatusBadge
                                status={waiver.waiver_status === 'paid' ? 'completed' : waiver.waiver_status}
                                label={waiver.waiver_status === 'paid' ? 'Reimbursement Received' : undefined}
                              />
                            </td>
                            <td className="table-cell">
                              {formatDate(waiver.created_at)}
                            </td>
                            <td className="table-cell">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => {
                                    setSelectedClaim(waiver);
                                    setShowClaimDetails(true);
                                  }}
                                  className="icon-button-admin"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                {waiver.waiver_status === 'pending' && (
                                  <button
                                    onClick={() => {
                                      setSelectedClaim(waiver);
                                      setShowSubmitModal(true);
                                    }}
                                    className="text-green-600 hover:text-green-900 hover:bg-green-50 p-2 rounded-full transition-all duration-200"
                                    title="Submit to MOM"
                                  >
                                    <Send className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Overseas Leave Tab */}
            {activeTab === 'overseas' && (
              <div className="space-y-6">
                <div className="alert alert-info">
                  <Globe className="alert-icon" />
                  <div>
                    <h3 className="text-sm font-medium">
                      Overseas Leave Waivers
                    </h3>
                    <div className="mt-2 text-sm">
                      <p>
                        Track overseas leave waivers for foreign workers. Workers must be away for at least 7 consecutive days,
                        capped at 60 calendar days per calendar year.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-content">
                    <div className="text-center py-12">
                      <Globe className="mx-auto h-12 w-12 text-blue-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Overseas Leave Management</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Overseas leave waiver functionality will be implemented here
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Medical Cases Tab */}
            {activeTab === 'medical' && (
              <div className="space-y-6">
                <div className="alert alert-warning">
                  <Activity className="alert-icon" />
                  <div>
                    <h3 className="text-sm font-medium">
                      Medical/Hospitalisation Waivers
                    </h3>
                    <div className="mt-2 text-sm">
                      <p>
                        Track hospitalisation leave waivers for foreign workers. Requires medical certificate with worker name,
                        FIN, period, institution name, doctor name, and MC number. Capped at 60 calendar days per calendar year.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-content">
                    <div className="text-center py-12">
                      <Activity className="mx-auto h-12 w-12 text-red-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Medical Cases Management</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Medical cases waiver functionality will be implemented here
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div className="space-y-6">
                <div className="alert alert-info">
                  <FileText className="alert-icon" />
                  <div>
                    <h3 className="text-sm font-medium">
                      Levy Waiver Documentation
                    </h3>
                    <div className="mt-2 text-sm">
                      <p>
                        Manage required documents for levy waiver applications. Track document submission status,
                        compliance requirements, and MOM documentation standards.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Document Categories */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Overseas Leave Documents */}
                  <div className="card">
                    <div className="card-header">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <Globe className="w-5 h-5 mr-2 text-blue-600" />
                        Overseas Leave Documents
                      </h3>
                    </div>
                    <div className="card-content">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span className="text-sm font-medium">Passport Copy</span>
                          </div>
                          <span className="text-xs text-green-600 font-medium">Required</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span className="text-sm font-medium">Flight Itinerary</span>
                          </div>
                          <span className="text-xs text-green-600 font-medium">Required</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span className="text-sm font-medium">Entry/Exit Stamps</span>
                          </div>
                          <span className="text-xs text-green-600 font-medium">Required</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium">Return Confirmation</span>
                          </div>
                          <span className="text-xs text-gray-500 font-medium">Optional</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Medical Documents */}
                  <div className="card">
                    <div className="card-header">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <Activity className="w-5 h-5 mr-2 text-red-600" />
                        Medical Documents
                      </h3>
                    </div>
                    <div className="card-content">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span className="text-sm font-medium">Medical Certificate</span>
                          </div>
                          <span className="text-xs text-green-600 font-medium">Required</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span className="text-sm font-medium">Hospital Admission Letter</span>
                          </div>
                          <span className="text-xs text-green-600 font-medium">Required</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span className="text-sm font-medium">Discharge Summary</span>
                          </div>
                          <span className="text-xs text-green-600 font-medium">Required</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium">Medical Reports</span>
                          </div>
                          <span className="text-xs text-gray-500 font-medium">Optional</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* General Documents */}
                  <div className="card">
                    <div className="card-header">
                      <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <FileText className="w-5 h-5 mr-2 text-gray-600" />
                        General Documents
                      </h3>
                    </div>
                    <div className="card-content">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span className="text-sm font-medium">Work Permit Copy</span>
                          </div>
                          <span className="text-xs text-green-600 font-medium">Required</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span className="text-sm font-medium">Employment Contract</span>
                          </div>
                          <span className="text-xs text-green-600 font-medium">Required</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium">Supporting Letters</span>
                          </div>
                          <span className="text-xs text-gray-500 font-medium">Optional</span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium">Additional Evidence</span>
                          </div>
                          <span className="text-xs text-gray-500 font-medium">Optional</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Document Upload Section */}
                <div className="card">
                  <div className="card-header">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                      <Upload className="w-5 h-5 mr-2 text-blue-600" />
                      Document Upload & Management
                    </h3>
                  </div>
                  <div className="card-content">
                    <div className="space-y-6">
                      {/* Upload Area */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-4">
                          <h3 className="text-lg font-medium text-gray-900">Upload Documents</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Drag and drop files here, or click to select files
                          </p>
                        </div>
                        <div className="mt-4">
                          <button className="btn btn-admin-primary">
                            <Upload className="w-4 h-4 mr-2" />
                            Choose Files
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          Supported formats: PDF, JPG, PNG (Max 5MB per file)
                        </p>
                      </div>

                      {/* Document Status Table */}
                      <div className="overflow-x-auto">
                        <table className="table">
                          <thead className="table-header">
                            <tr>
                              <th className="table-header-cell">Document Type</th>
                              <th className="table-header-cell">Employee</th>
                              <th className="table-header-cell">Upload Date</th>
                              <th className="table-header-cell">Status</th>
                              <th className="table-header-cell">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="table-body">
                            <tr className="table-row">
                              <td className="table-cell">
                                <div className="flex items-center">
                                  <FileText className="w-4 h-4 text-blue-600 mr-2" />
                                  Medical Certificate
                                </div>
                              </td>
                              <td className="table-cell">
                                <div className="text-sm font-medium text-gray-900">John Doe</div>
                                <div className="text-sm text-gray-500">WP1234567</div>
                              </td>
                              <td className="table-cell">
                                {formatDate(new Date())}
                              </td>
                              <td className="table-cell">
                                <StatusBadge status="verified" />
                              </td>
                              <td className="table-cell">
                                <div className="flex space-x-2">
                                  <button className="icon-button-admin" title="View">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button className="icon-button-admin" title="Download">
                                    <Download className="w-4 h-4" />
                                  </button>
                                  <button className="icon-button-danger" title="Delete">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            <tr className="table-row">
                              <td className="table-cell">
                                <div className="flex items-center">
                                  <Globe className="w-4 h-4 text-green-600 mr-2" />
                                  Flight Itinerary
                                </div>
                              </td>
                              <td className="table-cell">
                                <div className="text-sm font-medium text-gray-900">Jane Smith</div>
                                <div className="text-sm text-gray-500">WP7654321</div>
                              </td>
                              <td className="table-cell">
                                {formatDate(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000))}
                              </td>
                              <td className="table-cell">
                                <StatusBadge status="pending" label="Pending Review" />
                              </td>
                              <td className="table-cell">
                                <div className="flex space-x-2">
                                  <button className="icon-button-admin" title="View">
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button className="icon-button-admin" title="Download">
                                    <Download className="w-4 h-4" />
                                  </button>
                                  <button className="icon-button-danger" title="Delete">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Compliance Checklist */}
                      <div className="bg-yellow-50 p-4 rounded-2xl">
                        <h4 className="font-medium text-yellow-900 mb-3 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-2 text-yellow-600" />
                          MOM Compliance Checklist
                        </h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span>All documents must be in English or accompanied by certified translations</span>
                          </div>
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span>Medical certificates must include worker name, FIN, period, and MC number</span>
                          </div>
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span>Overseas leave must be at least 7 consecutive days</span>
                          </div>
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span>Total waiver period capped at 60 calendar days per year</span>
                          </div>
                          <div className="flex items-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                            <span>Submit applications within 30 days of worker's return</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* NS Leave (NS Make-Up Pay) Section */}
        {mainTab === 'ns-makeup' && (
          <NSMakeUpPayTab />
        )}
      </div>

      {/* Claim Details Modal */}
      {showClaimDetails && selectedClaim && (
        <ClaimDetailsModal
          claim={selectedClaim}
          onClose={() => setShowClaimDetails(false)}
          onUpdateStatus={handleUpdateStatus}
        />
      )}

      {/* Submit Modal */}
      {showSubmitModal && selectedClaim && (
        <SubmitModal
          claim={selectedClaim}
          onClose={() => setShowSubmitModal(false)}
          onSubmit={handleSubmitClaim}
        />
      )}

      {/* Bulk Submit Modal */}
      {showBulkSubmit && (
        <BulkSubmitModal
          claimCount={selectedClaims.length}
          onClose={() => setShowBulkSubmit(false)}
          onSubmit={mainTab === 'leave-reimbursement' ? handleBulkSubmit : handleBulkLevySubmit}
        />
      )}

      {/* Levy Waiver Details Modal */}
      {showClaimDetails && selectedClaim && mainTab === 'levy-waiver' && (
        <LevyWaiverDetailsModal
          waiver={selectedClaim}
          onClose={() => setShowClaimDetails(false)}
          onUpdateStatus={handleLevyWaiverStatusUpdate}
        />
      )}

      {/* Levy Waiver Submit Modal */}
      {showSubmitModal && selectedClaim && mainTab === 'levy-waiver' && (
        <LevyWaiverSubmitModal
          waiver={selectedClaim}
          onClose={() => setShowSubmitModal(false)}
          onSubmit={handleLevyWaiverSubmit}
        />
      )}
    </div>
  );
};

// Claim Details Modal Component
const ClaimDetailsModal = ({ claim, onClose, onUpdateStatus }) => {
  const [newStatus, setNewStatus] = useState('');
  const [processedDate, setProcessedDate] = useState(getTodayInSingaporeTimezone());

  const handleStatusUpdate = () => {
    if (newStatus) {
      onUpdateStatus(claim.id, {
        claim_status: newStatus,
        processed_date: processedDate
      });
      onClose();
    }
  };

  return (
    <Modal
      isOpen={!!claim}
      onClose={onClose}
      title="Claim Details"
      size="lg"
      theme="admin"
      footer={
        <>
          <button onClick={onClose} className="btn btn-admin-outline">
            <XCircle className="w-4 h-4 mr-2" />
            Close
          </button>
          {claim?.claim_status === 'submitted' && (
            <button
              onClick={handleStatusUpdate}
              disabled={!newStatus}
              className="btn btn-admin-primary"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Update Status
            </button>
          )}
        </>
      }
    >
      <div className="space-y-4">
          {/* Employee Info */}
          <div className="bg-gray-50 p-4 rounded-2xl">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <Users className="w-4 h-4 mr-2 text-gray-600" />
              Employee Information
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Name:</span>
                <span className="ml-2 font-medium">{claim.employee_details?.full_name}</span>
              </div>
              <div>
                <span className="text-gray-500">Staff ID:</span>
                <span className="ml-2 font-medium">{claim.employee_details?.staff_id}</span>
              </div>
              <div>
                <span className="text-gray-500">Department:</span>
                <span className="ml-2 font-medium">{claim.employee_details?.department}</span>
              </div>
              <div>
                <span className="text-gray-500">Classification:</span>
                <span className="ml-2 font-medium">{claim.employee_details?.employment_classification}</span>
              </div>
            </div>
          </div>

          {/* Leave Details */}
          <div className="bg-gray-50 p-4 rounded-2xl">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <Calendar className="w-4 h-4 mr-2 text-gray-600" />
              Leave Information
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Leave Type:</span>
                <span className="ml-2 font-medium">{claim.leave_type_details?.leave_type_name}</span>
              </div>
              <div>
                <span className="text-gray-500">Period:</span>
                <span className="ml-2 font-medium">
                  {formatDate(claim.leave_application_details?.leave_start_date)} - {formatDate(claim.leave_application_details?.leave_end_date)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Total Days:</span>
                <span className="ml-2 font-medium">{claim.leave_application_details?.leave_total_days}</span>
              </div>
              <div>
                <span className="text-gray-500">Leave Status:</span>
                <span className="ml-2 font-medium capitalize">{claim.leave_application_details?.leave_status}</span>
              </div>
            </div>
          </div>

          {/* Reimbursement Details */}
          <div className="bg-gray-50 p-4 rounded-2xl">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <DollarSign className="w-4 h-4 mr-2 text-gray-600" />
              Reimbursement Details
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Employer Paid Weeks:</span>
                <span className="ml-2 font-medium">{claim.employer_paid_weeks}</span>
              </div>
              <div>
                <span className="text-gray-500">Government Reimbursed Weeks:</span>
                <span className="ml-2 font-medium">{claim.government_reimbursed_weeks}</span>
              </div>
              <div>
                <span className="text-gray-500">Total Amount:</span>
                <span className="ml-2 font-medium">
                  {new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(claim.total_reimbursement_amount || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Reference Number:</span>
                <span className="ml-2 font-medium">{claim.claim_reference_number || 'Not assigned'}</span>
              </div>
            </div>
          </div>

          {/* Status Update */}
          {claim.claim_status === 'submitted' && (
            <div className="bg-red-50 p-4 rounded-2xl">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                <Settings className="w-4 h-4 mr-2 text-red-600" />
                Update Status
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">New Status</label>
                  <FormSelect
                    options={[
                      { value: '', label: 'Select status' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'rejected', label: 'Rejected' },
                      { value: 'paid', label: 'Paid' },
                    ]}
                    value={newStatus}
                    onChange={setNewStatus}
                    theme="admin"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Processed Date</label>
                  <input
                    type="date"
                    value={processedDate}
                    onChange={(e) => setProcessedDate(e.target.value)}
                    className="input-admin"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
    </Modal>
  );
};

// Submit Modal Component
const SubmitModal = ({ claim, onClose, onSubmit }) => {
  const [referenceNumber, setReferenceNumber] = useState('');
  const [submittedDate, setSubmittedDate] = useState(getTodayInSingaporeTimezone());

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    onSubmit(claim.id, {
      claim_reference_number: referenceNumber,
      submitted_date: submittedDate
    });
  };

  return (
    <Modal
      isOpen={!!claim}
      onClose={onClose}
      title="Submit Claim to Government"
      size="md"
      theme="admin"
      footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-secondary">
            <XCircle className="w-4 h-4 mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            form="submit-claim-form"
            className="btn btn-admin-primary"
          >
            <Send className="w-4 h-4 mr-2" />
            Submit Claim
          </button>
        </div>
      }
    >
      <form id="submit-claim-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="form-group">
          <label className="form-label">
            <FileText className="w-4 h-4 inline mr-2" />
            Government Reference Number
          </label>
          <input
            type="text"
            placeholder="e.g., GOV2024001234"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            className="input-admin"
          />
        </div>

        <div className="form-group">
          <label className="form-label">
            <Calendar className="w-4 h-4 inline mr-2" />
            Submission Date
          </label>
          <input
            type="date"
            value={submittedDate}
            onChange={(e) => setSubmittedDate(e.target.value)}
            className="input-admin"
          />
        </div>

        <div className="bg-red-50 p-3 rounded-2xl">
          <p className="text-sm text-red-800">
            <strong className="flex items-center">
              <Users className="w-4 h-4 mr-1" />
              Employee:
            </strong> {claim.employee_details?.full_name}<br/>
            <strong className="flex items-center">
              <DollarSign className="w-4 h-4 mr-1" />
              Amount:
            </strong> {new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(claim.total_reimbursement_amount || 0)}
          </p>
        </div>
      </form>
    </Modal>
  );
};

// Bulk Submit Modal Component
const BulkSubmitModal = ({ claimCount, onClose, onSubmit }) => {
  const [submittedDate, setSubmittedDate] = useState(getTodayInSingaporeTimezone());

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    onSubmit({
      submitted_date: submittedDate
    });
  };

  return (
    <Modal
      isOpen={!!claimCount}
      onClose={onClose}
      title="Bulk Submit Claims"
      size="md"
      theme="admin"
      footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-secondary">
            <XCircle className="w-4 h-4 mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            form="bulk-submit-form"
            className="btn btn-success"
          >
            <Send className="w-4 h-4 mr-2" />
            Submit {claimCount} Claims
          </button>
        </div>
      }
    >
      <form id="bulk-submit-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="alert alert-warning">
          <AlertTriangle className="alert-icon" />
          <div>
            <p className="text-sm">
              You are about to submit <strong>{claimCount} claims</strong> to the government.
              This action will change their status to "Submitted" and cannot be undone.
            </p>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            <Calendar className="w-4 h-4 inline mr-2" />
            Submission Date
          </label>
          <input
            type="date"
            value={submittedDate}
            onChange={(e) => setSubmittedDate(e.target.value)}
            className="input-admin"
          />
        </div>
      </form>
    </Modal>
  );
};

// Levy Waiver Details Modal Component
const LevyWaiverDetailsModal = ({ waiver, onClose, onUpdateStatus }) => {
  const [newStatus, setNewStatus] = useState('');
  const [processedDate, setProcessedDate] = useState(getTodayInSingaporeTimezone());
  const [momComments, setMomComments] = useState('');

  const handleStatusUpdate = () => {
    if (newStatus) {
      onUpdateStatus(waiver.id, {
        waiver_status: newStatus,
        processed_date: processedDate,
        mom_comments: momComments
      });
      onClose();
    }
  };

  return (
    <Modal
      isOpen={!!waiver}
      onClose={onClose}
      title="Levy Waiver Details"
      size="lg"
      theme="admin"
      footer={
        <>
          <button onClick={onClose} className="btn btn-admin-outline">
            <XCircle className="w-4 h-4 mr-2" />
            Close
          </button>
          {waiver?.waiver_status === 'submitted' && (
            <button
              onClick={handleStatusUpdate}
              disabled={!newStatus}
              className="btn btn-admin-primary"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Update Status
            </button>
          )}
        </>
      }
    >
      <div className="space-y-4">
          {/* Employee Info */}
          <div className="bg-gray-50 p-4 rounded-2xl">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <Users className="w-4 h-4 mr-2 text-gray-600" />
              Employee Information
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Name:</span>
                <span className="ml-2 font-medium">{waiver.employee_details?.full_name}</span>
              </div>
              <div>
                <span className="text-gray-500">Staff ID:</span>
                <span className="ml-2 font-medium">{waiver.employee_details?.staff_id}</span>
              </div>
              <div>
                <span className="text-gray-500">Department:</span>
                <span className="ml-2 font-medium">{waiver.employee_details?.department}</span>
              </div>
              <div>
                <span className="text-gray-500">Work Permit:</span>
                <span className="ml-2 font-medium">{waiver.work_permit_number}</span>
              </div>
            </div>
          </div>

          {/* Waiver Details */}
          <div className="bg-gray-50 p-4 rounded-2xl">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <Shield className="w-4 h-4 mr-2 text-gray-600" />
              Waiver Information
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Type:</span>
                <span className="ml-2 font-medium">{waiver.waiver_type_display}</span>
              </div>
              <div>
                <span className="text-gray-500">Period:</span>
                <span className="ml-2 font-medium">
                  {formatDate(waiver.waiver_start_date)} - {formatDate(waiver.waiver_end_date)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Total Days:</span>
                <span className="ml-2 font-medium">{waiver.waiver_total_days}</span>
              </div>
              <div>
                <span className="text-gray-500">Levy Rate:</span>
                <span className="ml-2 font-medium">
                  {new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(waiver.levy_rate_per_month || 0)}/month
                </span>
              </div>
              <div>
                <span className="text-gray-500">Amount Waived:</span>
                <span className="ml-2 font-medium">
                  {new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(waiver.levy_amount_waived || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>
                <span className="ml-2 font-medium capitalize">{waiver.waiver_status}</span>
              </div>
            </div>
          </div>

          {/* Type-specific details */}
          {waiver.waiver_type === 'overseas_leave' && (
            <div className="bg-blue-50 p-4 rounded-2xl">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                <Globe className="w-4 h-4 mr-2 text-blue-600" />
                Overseas Leave Details
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Destination:</span>
                  <span className="ml-2 font-medium">{waiver.overseas_destination || 'Not specified'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Return Date:</span>
                  <span className="ml-2 font-medium">{waiver.overseas_return_date ? formatDate(waiver.overseas_return_date) : 'Not returned'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Did Return:</span>
                  <span className="ml-2 font-medium">{waiver.overseas_did_return ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>
          )}

          {waiver.waiver_type === 'hospitalisation' && (
            <div className="bg-red-50 p-4 rounded-2xl">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                <Activity className="w-4 h-4 mr-2 text-red-600" />
                Medical Details
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Institution:</span>
                  <span className="ml-2 font-medium">{waiver.medical_institution || 'Not specified'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Doctor:</span>
                  <span className="ml-2 font-medium">{waiver.doctor_name || 'Not specified'}</span>
                </div>
                <div>
                  <span className="text-gray-500">MC Number:</span>
                  <span className="ml-2 font-medium">{waiver.mc_number || 'Not specified'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Status Update */}
          {waiver.waiver_status === 'submitted' && (
            <div className="bg-blue-50 p-4 rounded-2xl">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                <Settings className="w-4 h-4 mr-2 text-blue-600" />
                Update Status
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">New Status</label>
                  <FormSelect
                    options={[
                      { value: '', label: 'Select status' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'rejected', label: 'Rejected' },
                    ]}
                    value={newStatus}
                    onChange={setNewStatus}
                    theme="admin"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Processed Date</label>
                  <input
                    type="date"
                    value={processedDate}
                    onChange={(e) => setProcessedDate(e.target.value)}
                    className="input-admin"
                  />
                </div>
              </div>
              <div className="form-group mt-3">
                <label className="form-label">MOM Comments</label>
                <textarea
                  value={momComments}
                  onChange={(e) => setMomComments(e.target.value)}
                  className="textarea-admin"
                  rows="3"
                  placeholder="Comments from MOM..."
                />
              </div>
            </div>
          )}

          {/* Required Documents */}
          <div className="bg-yellow-50 p-4 rounded-2xl">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <FileText className="w-4 h-4 mr-2 text-yellow-600" />
              Required Documents
            </h4>
            <ul className="text-sm space-y-1">
              {waiver.required_documents?.map((doc, index) => (
                <li key={index} className="flex items-center">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                  {doc}
                </li>
              ))}
            </ul>
          </div>

          {/* Eligibility Errors */}
          {waiver.eligibility_errors?.length > 0 && (
            <div className="bg-red-50 p-4 rounded-2xl">
              <h4 className="font-medium text-red-900 mb-2 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2 text-red-600" />
                Eligibility Issues
              </h4>
              <ul className="text-sm space-y-1 text-red-700">
                {waiver.eligibility_errors.map((error, index) => (
                  <li key={index} className="flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
    </Modal>
  );
};

// Levy Waiver Submit Modal Component
const LevyWaiverSubmitModal = ({ waiver, onClose, onSubmit }) => {
  const [momReferenceNumber, setMomReferenceNumber] = useState('');
  const [submittedDate, setSubmittedDate] = useState(getTodayInSingaporeTimezone());

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    onSubmit(waiver.id, {
      mom_reference_number: momReferenceNumber,
      submitted_date: submittedDate
    });
  };

  return (
    <Modal
      isOpen={!!waiver}
      onClose={onClose}
      title="Submit Waiver to MOM"
      size="md"
      theme="admin"
      footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn btn-secondary">
            <XCircle className="w-4 h-4 mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            form="levy-waiver-submit-form"
            className="btn btn-admin-primary"
          >
            <Send className="w-4 h-4 mr-2" />
            Submit Waiver
          </button>
        </div>
      }
    >
      <form id="levy-waiver-submit-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="form-group">
          <label className="form-label">
            <FileText className="w-4 h-4 inline mr-2" />
            MOM Reference Number
          </label>
          <input
            type="text"
            placeholder="e.g., MOM2024001234"
            value={momReferenceNumber}
            onChange={(e) => setMomReferenceNumber(e.target.value)}
            className="input-admin"
          />
        </div>

        <div className="form-group">
          <label className="form-label">
            <Calendar className="w-4 h-4 inline mr-2" />
            Submission Date
          </label>
          <input
            type="date"
            value={submittedDate}
            onChange={(e) => setSubmittedDate(e.target.value)}
            className="input-admin"
          />
        </div>

        <div className="bg-blue-50 p-3 rounded-2xl">
          <p className="text-sm text-blue-800">
            <strong className="flex items-center">
              <Users className="w-4 h-4 mr-1" />
              Employee:
            </strong> {waiver.employee_details?.full_name}<br/>
            <strong className="flex items-center">
              <Shield className="w-4 h-4 mr-1" />
              Type:
            </strong> {waiver.waiver_type_display}<br/>
            <strong className="flex items-center">
              <DollarSign className="w-4 h-4 mr-1" />
              Amount:
            </strong> {new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(waiver.levy_amount_waived || 0)}
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default GovernmentReimbursement; 